import update from 'immutability-helper';
import get from 'lodash.get';
import moment from 'moment';
import { createSelector } from 'reselect';

import { LayerVisibility } from '../models/Layer';
import { PopupState } from '../models/PopupState';
import agreementFilter from '../services/agreementFilter';
import { isAgreementHighlightLayer, isAgreementLayer } from '../utils/layerMatch';
import { State } from './reducer';

export const getMapStyleBase = ({ mapStyleBase }: State) => mapStyleBase;

export const getMapLocation = ({ map }: State) => map;

export const getMapPopupLayers = ({ mapPopupLayers }: State) => mapPopupLayers;

export const getMapLayerBounds = ({ mapLayerBounds }: State) => mapLayerBounds;

export const getAgreements = ({ agreements }: State) => agreements;

export const getIsAgreementsTableOpen = ({ isAgreementsTableOpen }: State) => isAgreementsTableOpen;

export const getAgreementByFileNumber = (fileNumber: string) =>
  createSelector(
    getAgreements,
    agreements => {
      const match = Object.keys(agreements).find(id => agreements[id].fileNumber === fileNumber);
      return match ? agreements[match] : null;
    }
  );

export const getAgreementsPopupTab = ({ agreementsPopupTab }: State) => agreementsPopupTab;

export const getAgreementsTableState = ({ agreementsTable }: State) => agreementsTable;

export const getBasemapStyles = ({ basemapStyles }: State) => basemapStyles;

export const getBasemapStyleNames = createSelector(
  getBasemapStyles,
  basemapStyles => basemapStyles.filter(style => !!style.name).map(style => style.name!)
);

export const getBasemapStyleByName = (name: string) =>
  createSelector(
    getBasemapStyles,
    basemapStyles => {
      return basemapStyles.find(style => style.name === name);
    }
  );

/** Get number of all possible agreements */
export const getTotalAgreementsNumber = createSelector(
  getAgreements,
  agreements => Object.keys(agreements).length
);

export const getSelectedAgreementIdState = ({ selectedAgreementId }: State) => selectedAgreementId;

export const getSelectedAgreement = createSelector(
  getAgreements,
  getSelectedAgreementIdState,
  (agreementEntities, selectedId) => (selectedId ? agreementEntities[selectedId] : null)
);

export const getSelectedTableAgreementMapLocation = ({
  selectedTableAgreementMapLocation
}: State) => selectedTableAgreementMapLocation;

// Identify

export const getIdentify = ({ identify }: State) => identify;

export const getIsPopupLoading = ({ isPopupLoading }: State) => isPopupLoading;

export const getIdentifyLocation = createSelector(
  getIdentify,
  ({ location }) => location
);

/** All features in the current identify state. Features at last map click. */
export const getIdentifyFeatures = createSelector(
  getIdentify,
  ({ entities }) => Object.keys(entities).map(key => entities[key])
);

/** Focused/selected identify feature with props displayed in identify map popup */
export const getSelectedIdentifyFeature = createSelector(
  getIdentify,
  ({ entities, selectedId }) => (selectedId ? entities[selectedId] : null)
);

/**
 * Determine if popup is single, agreement, or multiple and select data needed for each type.
 * Not yet using actual agreement data, just data from feature clicked.
 * For additional agreement data see {@link selectors#getIdentifyPopupWithAgreement | getIdentifyPopupWithAgreement}.
 */
export const getIdentifyPopup = createSelector(
  getIdentifyLocation,
  getIdentifyFeatures,
  getSelectedIdentifyFeature,
  (location, features, selectedFeature): PopupState | null => {
    if (selectedFeature) {
      const isAgreement = isAgreementLayer(selectedFeature.layer);
      return {
        popupType: isAgreement ? 'agreement' : 'single',
        location,
        data: selectedFeature.properties,
        canGoBack: features.length > 1
      };
    }

    if (features.length) {
      return {
        popupType: 'multiple',
        location,
        data: features.map((f): { id: string; title: string } => ({
          id: f.id,
          title: f.properties._title
        })),
        canGoBack: features.length > 1
      };
    }

    // No features, count === 0
    return null;
  }
);

/**
 * Popup data based on identify state. Same as {@link selectors#getIdentifyPopup | getIdentifyPopup }
 * but with agreement data for agreement type popups
 */
export const getIdentifyPopupWithAgreement = createSelector(
  getIdentifyPopup,
  getAgreements,
  (popupData, agreements): PopupState | null => {
    if (!popupData) {
      return null;
    }

    if (popupData.popupType !== 'agreement') {
      // Generic/non-agreement popup or multiple. Nothing else needed.
      return popupData;
    }

    const fileNo = get(popupData, 'data["File Number"]', null);

    if (!fileNo) {
      // No 'File Number' on the clicked agreement
      return {
        ...popupData,
        data: null
      };
    }

    const matchAgreementId = Object.keys(agreements).find(
      id => agreements[id].fileNumber === fileNo
    );

    return {
      ...popupData,
      data: matchAgreementId ? agreements[matchAgreementId] : null
    };
  }
);

export const getCurrentSidebarTab = ({ sidebarTab }: State) => sidebarTab;

export const getTitleState = ({ title }: State) => title;

export const getLaserficheCredentials = ({ laserficheCredentials }: State) => laserficheCredentials;

export const getLaserficheDisplayInfo = createSelector(
  getLaserficheCredentials,
  laserficheCredentials => {
    return {
      username: laserficheCredentials.username,
      initials: laserficheCredentials.initials
    };
  }
);

export const getLaserficheSignInInfo = createSelector(
  getLaserficheCredentials,
  laserficheCredentials => {
    return {
      username: laserficheCredentials.username,
      password: laserficheCredentials.password
    };
  }
);

export const getSignInDialogState = ({ signInDialog }: State) => signInDialog;

export const getSearchFields = ({ searchFields }: State) => searchFields;

export const getCompanyOptions = ({ companyOptions }: State) => companyOptions;

export const getToggleableLayers = ({ toggleableLayers }: State) => toggleableLayers;

export const getDocumentsRequested = ({ documentsRequested }: State) => documentsRequested;

export const getToggleableLayersVisibilities = createSelector(
  getToggleableLayers,
  toggleableLayers =>
    toggleableLayers.reduce<LayerVisibility[]>(
      (acc, toggleableLayer) => [
        ...acc,
        ...toggleableLayer.controlledLayerIds.map(l => ({
          layerId: l,
          visible: toggleableLayer.isChecked
        }))
      ],
      []
    )
);

export const getFilteredSearchFields = createSelector(
  getSearchFields,
  searchFields => {
    return searchFields.filter(field => {
      switch (field.fieldName) {
        case 'Company Name':
        case 'File No':
        case 'Status':
          return field.value.length !== 0;
        case 'Agreement Date':
          return moment(field.value, 'MM-DD-YYYY').isBetween('1901-01-01', '2050-01-01', 'year');
        default:
          return field;
      }
    });
  }
);

export const getFilteredAgreementIds = createSelector(
  getAgreements,
  getFilteredSearchFields,
  (agreements, searchFields) => {
    if (!searchFields.length) {
      return Object.keys(agreements);
    }

    const filteredIds: string[] = [];
    for (const agreementId in agreements) {
      if (agreementFilter(agreements[agreementId], searchFields)) {
        filteredIds.push(agreementId);
      }
    }
    return filteredIds;
  }
);

export const getFilteredAgreements = createSelector(
  getAgreements,
  getFilteredAgreementIds,
  (agreements, ids) => {
    return ids.map(id => {
      return { ...agreements[id] };
    });
  }
);

export const getFilteredAgreementsNumber = createSelector(
  getFilteredAgreements,
  filteredAgreements => filteredAgreements.length
);

export const getFilteredFileNumbers = createSelector(
  getAgreements,
  getFilteredAgreementIds,
  (agreements, ids) => {
    return ids.map(id => agreements[id].fileNumber);
  }
);

export const getfilteredAndSortedAgreements = createSelector(
  getFilteredAgreements,
  getAgreementsTableState,
  (agreements, { order, orderBy }) => {
    return agreements.sort((a, b) => {
      if (orderBy === 'agreementDate') {
        if (moment(a[orderBy], 'L').diff(moment(b[orderBy], 'L')) > 0) {
          return order === 'asc' ? 1 : -1;
        }
        if (moment(a[orderBy], 'L').diff(moment(b[orderBy], 'L')) < 0) {
          return order === 'asc' ? -1 : 1;
        }
        return 0;
      }
      if (a[orderBy] !== null && b[orderBy] === null) {
        return order === 'asc' ? 1 : -1;
      }
      if (a[orderBy] === null && b[orderBy] !== null) {
        return order === 'asc' ? -1 : 1;
      }
      if (a[orderBy] === null && b[orderBy] === null) {
        return 0;
      }
      if (a[orderBy] === null || b[orderBy] === null) {
        return order === 'asc' ? 1 : -1;
      }
      if (a[orderBy].toLowerCase() > b[orderBy].toLowerCase()) {
        return order === 'asc' ? 1 : -1;
      }
      if (a[orderBy].toLowerCase() < b[orderBy].toLowerCase()) {
        return order === 'asc' ? -1 : 1;
      }
      return 0;
    });
  }
);

export const getMapStyle = createSelector(
  getMapStyleBase,
  getToggleableLayersVisibilities,
  getFilteredFileNumbers,
  getSelectedAgreement,
  (mapStyleBase, toggleableLayersVisibilities, filteredFileNumbers, selectedAgreement) => {
    return update<any>(mapStyleBase, {
      layers: (layers: any[]) => {
        const visibilityMap: any = toggleableLayersVisibilities.reduce(
          (acc, l) => ({ ...acc, [l.layerId]: l }),
          {}
        );
        const newLayers = layers.reduce((acc: any, layer: any, i: number) => {
          const visibleLayer = visibilityMap[layer.id];

          if (isAgreementLayer(layer)) {
            // Set filter on agreement layers to only show currently filtered file numbers

            if (!visibleLayer) {
              return {
                ...acc,
                [i]: {
                  filter: {
                    $set: ['in', 'File Number', ...filteredFileNumbers]
                  }
                }
              };
            }

            return {
              ...acc,
              [i]: {
                layout: {
                  visibility: {
                    $set: visibleLayer && visibleLayer.visible ? 'visible' : 'none'
                  }
                },
                filter: {
                  $set: ['in', 'File Number', ...filteredFileNumbers]
                }
              }
            };
          }

          if (isAgreementHighlightLayer(layer)) {
            // Set filter on highlight layers to only show currently selected agreements
            return {
              ...acc,
              [i]: {
                filter: {
                  $set: ['==', 'File Number', get(selectedAgreement, 'fileNumber', 'NOMATCH')]
                }
              }
            };
          }

          if (!visibleLayer) {
            return acc;
          }

          return {
            ...acc,
            [i]: {
              layout: {
                visibility: {
                  $set: visibleLayer.visible ? 'visible' : 'none'
                }
              }
            }
          };
        }, {});

        return update(layers, newLayers);
      }
    });
  }
);

export const getTodoActions = ({ todoActions }: State) => todoActions;
